import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Trophy, Users, Calendar, Crown, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function SorteosExclusivos() {
  const { user } = useAuth();
  const [raffles, setRaffles] = useState(null);
  const [entered, setEntered] = useState({});
  const [busy, setBusy] = useState(null);

  const canParticipate = user && user.is_member;

  const load = () =>
    api.get("/raffles", { params: { type: "exclusive" } }).then(({ data }) => setRaffles(data)).catch(() => setRaffles([]));

  useEffect(() => {
    load();
  }, []);

  const enter = async (raffleId) => {
    setBusy(raffleId);
    try {
      const { data } = await api.post(`/raffles/${raffleId}/enter-exclusive`);
      toast.success(data.message);
      setEntered((p) => ({ ...p, [raffleId]: true }));
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24" data-testid="sorteos-exclusivos-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <p className="overline-gold mb-4 flex items-center gap-2"><Crown size={14} /> Solo socios</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight">
          Sorteos <span className="text-gold italic">Exclusivos</span>
        </h1>
        <p className="mt-5 text-white/60 font-light max-w-2xl">
          Premios de otro nivel, solo para miembros del club. Hazte socio por 100 €/año y participa en todos los sorteos.
        </p>
      </motion.div>

      {/* SORTEOS ACTIVOS — tarjetas del club */}
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
        className="mt-14 border border-gold/25 bg-forest/60 p-7 lg:p-10" data-testid="sorteos-activos-block">
        <p className="overline-gold mb-2">Sorteos activos</p>
        <h2 className="text-2xl lg:text-3xl text-white font-light">Tarjetas en juego esta temporada</h2>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { qty: "2", name: "Tarjetas de Aguardos en Finca" },
            { qty: "2", name: "Tarjetas de Temporada de Zorzal" },
            { qty: "2", name: "Tarjetas Temporada Media Veda" },
            { qty: "2", name: "Tarjetas Reclamo de Perdiz" },
            { qty: "5", name: "Tarjetas Puesto Palomas en Comedero" },
            { qty: "2", name: "Tarjetas Puesto Suelta de Perdices" },
          ].map((t) => (
            <div key={t.name} className="flex items-center gap-4 bg-carbon/60 border border-white/10 px-5 py-4" data-testid={`tarjeta-${t.name.toLowerCase().replaceAll(" ", "-")}`}>
              <span className="font-heading text-4xl text-gold leading-none">{t.qty}</span>
              <span className="text-white/80 text-sm font-light">{t.name}</span>
            </div>
          ))}
        </div>
        <p className="mt-6 text-white/45 text-xs font-light uppercase tracking-[0.15em]">Finca Los Perales · Ruta del Agua · Guillena (Sevilla)</p>
      </motion.div>

      {raffles === null ? (
        <div className="py-24 flex justify-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>
      ) : raffles.length === 0 ? (
        <p className="py-24 text-white/40" data-testid="no-exclusive-raffles">No hay sorteos exclusivos abiertos ahora mismo.</p>
      ) : (
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {raffles.map((r, i) => (
            <motion.div key={r.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: i * 0.08 }}
              className="card-dark overflow-hidden" data-testid={`exclusive-raffle-${r.id}`}>
              <div className="relative h-64 bg-forest">
                {r.image && <img src={r.image} alt={r.title} className="w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/20 to-transparent" />
                {!canParticipate && (
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] flex flex-col items-center justify-center gap-4" data-testid={`exclusive-lock-${r.id}`}>
                    <Lock size={32} strokeWidth={1.25} className="text-gold" />
                    <p className="text-white/80 text-sm font-light">Contenido exclusivo para socios</p>
                    <Link to="/hazte-socio" className="btn-gold !px-6 !py-2.5" data-testid={`exclusive-unlock-${r.id}`}>Hazte Socio</Link>
                  </div>
                )}
                {r.status === "closed" && canParticipate && (
                  <span className="absolute top-4 left-4 bg-red-900/80 text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">Cerrado</span>
                )}
                {r.winner && (
                  <div className="absolute bottom-4 left-4 bg-gold text-black text-xs font-bold px-4 py-2" data-testid={`exclusive-winner-${r.id}`}>
                    Ganador: {r.winner.name}
                  </div>
                )}
              </div>
              <div className="p-7">
                <h2 className="text-2xl lg:text-3xl text-white font-medium">{r.title}</h2>
                <p className="mt-2 text-sand text-sm flex items-center gap-2"><Trophy size={15} strokeWidth={1.5} /> {r.prize}</p>
                <p className="mt-4 text-white/60 text-sm font-light leading-relaxed">{r.description}</p>
                <div className="mt-5 flex items-center gap-6 text-xs text-white/40 uppercase tracking-[0.15em]">
                  <span className="flex items-center gap-2"><Users size={14} /> {r.participants_count} socios</span>
                  {r.draw_date && <span className="flex items-center gap-2"><Calendar size={14} /> Sorteo: {r.draw_date}</span>}
                </div>
                {canParticipate && r.status === "open" && (
                  entered[r.id] ? (
                    <p className="mt-7 flex items-center gap-2 text-gold text-sm" data-testid={`exclusive-entered-${r.id}`}>
                      <CheckCircle2 size={18} /> Ya estás participando
                    </p>
                  ) : (
                    <button disabled={busy === r.id} onClick={() => enter(r.id)} className="btn-gold-solid mt-7 w-full disabled:opacity-40"
                      data-testid={`exclusive-join-${r.id}`}>
                      {busy === r.id ? "Registrando..." : "Participar como Socio"}
                    </button>
                  )
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
